import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "react-query";
import axios from "axios";
import AvatarImage from "../ImageUploaders/AvatarImage";
import { NotificationContext } from "../../App";
import { logout } from "../../Redux/actions/Auth";
import { brandColors } from "../../theme";
import { useSidebarAccent } from "./SidebarAccentContext";

export default function TopBar({
  setTheme,
  currentTheme,
  user,
  token,
  onMenuClick,
  topbarHeight = 60,
}) {
  const theme = useTheme();
  const router = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const isDark = theme.palette.mode === "dark";
  const { accent, setAccent, palette } = useSidebarAccent();

  const { notifications } = useContext(NotificationContext) || {
    notifications: [],
  };
  const unread = useMemo(
    () => (notifications || []).filter((n) => n.read === false).length,
    [notifications],
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [paletteAnchor, setPaletteAnchor] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const paletteOpen = Boolean(paletteAnchor);

  // Close menus when route changes
  useEffect(() => {
    setAnchorEl(null);
    setPaletteAnchor(null);
  }, [location.pathname]);

  // Logout flow
  const userUUID = user?.uuid;
  const logoutMutation = useMutation(() =>
    axios.post(
      `${process.env.REACT_APP_PUBLIC_URL}/auth/${userUUID}/logout`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    ),
  );
  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (e) {
      // ignore — proceed with local logout regardless
    }
    dispatch(logout());
    router.push("/login");
  };

  const displayName =
    user?.username || user?.name || user?.email?.split("@")[0] || "User";

  return (
    <Box
      component="header"
      sx={{
        flexShrink:      0,
        height:          topbarHeight,
        px:              { xs: 1.5, md: 2 },
        py:              1.25,
        backgroundColor: "transparent",
        borderBottom:    `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ height: "100%" }}
      >
        {/* Hamburger toggle */}
        {onMenuClick && (
          <Tooltip title="Toggle sidebar" arrow>
            <IconButton
              onClick={onMenuClick}
              size="small"
              sx={{
                color: "text.secondary",
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                borderRadius: 2,
                "&:hover": {
                  color: accent,
                  bgcolor: alpha(accent, 0.12),
                },
              }}
            >
              <MenuRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        {/* Spacer — pushes controls to the right */}
        <Box sx={{ flex: 1 }} />

        {/* Theme palette picker */}
        <Tooltip title="Sidebar accent" arrow>
          <IconButton
            size="small"
            onClick={(e) => setPaletteAnchor(e.currentTarget)}
            sx={{
              color: "text.secondary",
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: 2,
              "&:hover": {
                color: accent,
                bgcolor: alpha(accent, 0.14),
              },
            }}
          >
            <PaletteOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Theme toggle */}
        <Tooltip title={`Switch to ${currentTheme ? "dark" : "light"} mode`} arrow>
          <IconButton
            size="small"
            onClick={() => setTheme && setTheme(!currentTheme)}
            sx={{
              color: "text.secondary",
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: 2,
              "&:hover": {
                color: brandColors.vividViolet,
                bgcolor: alpha(brandColors.vividViolet, 0.14),
              },
            }}
          >
            {currentTheme ? (
              <DarkModeOutlinedIcon fontSize="small" />
            ) : (
              <LightModeOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>

        {/* Notifications */}
        <Tooltip title="Notifications" arrow>
          <IconButton
            size="small"
            onClick={() => router.push("/notifications")}
            sx={{
              color: "text.secondary",
              bgcolor: alpha(theme.palette.text.primary, 0.04),
              borderRadius: 2,
              "&:hover": {
                color: accent,
                bgcolor: alpha(accent, 0.12),
              },
            }}
          >
            <Badge
              badgeContent={unread}
              color="error"
              overlap="circular"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "0.62rem",
                  height: 16,
                  minWidth: 16,
                  px: 0.5,
                  fontWeight: 700,
                },
              }}
            >
              <NotificationsNoneOutlinedIcon fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Avatar menu */}
        <Tooltip title="Account" arrow>
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            size="small"
            sx={{
              p: 0.25,
              ml: 0.5,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "50%",
              transition: "border-color .2s ease, transform .2s ease",
              "&:hover": {
                borderColor: alpha(accent, 0.6),
                transform: "scale(1.05)",
              },
            }}
          >
            {user?.uuid ? (
              <AvatarImage size="small" uuid={user.uuid} token={token} />
            ) : (
              <Avatar sx={{ width: 32, height: 32 }}>
                {(displayName || "U").charAt(0).toUpperCase()}
              </Avatar>
            )}
          </IconButton>
        </Tooltip>

        <Menu
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            sx: {
              mt: 1.25,
              minWidth: 220,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: isDark
                ? "0 16px 40px rgba(0,0,0,0.5)"
                : "0 16px 40px rgba(15,23,42,0.12)",
              backgroundImage: "none",
              backgroundColor: isDark
                ? alpha(theme.palette.background.paper, 0.95)
                : theme.palette.background.paper,
              overflow: "hidden",
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.62rem",
              }}
            >
              Signed in as
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }} noWrap>
              {displayName}
            </Typography>
            {user?.email && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary" }}
                noWrap
                display="block"
              >
                {user.email}
              </Typography>
            )}
          </Box>
          <Divider sx={{ opacity: 0.6 }} />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              router.push("/profile");
            }}
            sx={{ py: 1, fontSize: "0.85rem" }}
          >
            <ListItemIcon>
              <PersonOutlineOutlinedIcon fontSize="small" />
            </ListItemIcon>
            My Profile
          </MenuItem>
          <Divider sx={{ opacity: 0.6 }} />
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              setLogoutOpen(true);
            }}
            sx={{
              py: 1,
              fontSize: "0.85rem",
              color: "error.main",
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            <ListItemIcon>
              <LogoutOutlinedIcon fontSize="small" sx={{ color: "error.main" }} />
            </ListItemIcon>
            Log out
          </MenuItem>
        </Menu>

        {/* Sidebar accent palette popover */}
        <Popover
          anchorEl={paletteAnchor}
          open={paletteOpen}
          onClose={() => setPaletteAnchor(null)}
          transformOrigin={{ horizontal: "right", vertical: "top" }}
          anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          PaperProps={{
            sx: {
              mt: 1.25,
              p: 2,
              width: 240,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: isDark
                ? "0 16px 40px rgba(0,0,0,0.5)"
                : "0 16px 40px rgba(15,23,42,0.12)",
              backgroundImage: "none",
              backgroundColor: isDark
                ? alpha(theme.palette.background.paper, 0.95)
                : theme.palette.background.paper,
            },
          }}
        >
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "text.secondary",
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontSize: "0.62rem",
              mb: 1,
            }}
          >
            Sidebar accent
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 1,
            }}
          >
            {palette.map((c) => {
              const selected = c.value === accent;
              return (
                <Tooltip key={c.value} title={c.name} arrow>
                  <Box
                    role="button"
                    aria-label={c.name}
                    onClick={() => {
                      setAccent(c.value);
                      setPaletteAnchor(null);
                    }}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      cursor: "pointer",
                      backgroundColor: c.value,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      border: `2px solid ${
                        selected ? theme.palette.text.primary : "transparent"
                      }`,
                      boxShadow: `0 4px 14px ${alpha(c.value, 0.45)}`,
                      transition: "transform .15s ease, border-color .15s ease",
                      "&:hover": { transform: "scale(1.08)" },
                    }}
                  >
                    {selected && <CheckRoundedIcon sx={{ fontSize: 20 }} />}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Popover>
      </Stack>

      {/* Logout confirm */}
      <Dialog
        open={logoutOpen}
        onClose={() => setLogoutOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            p: 1,
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm log out</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You're about to log out of your account. Any unsaved changes may be
            lost. You can log back in at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setLogoutOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Stay logged in
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleLogout}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Yes, log me out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
